import dayjs from 'dayjs'

import { formatMoney, formatMoneyShort } from '@percent/utility'

import {
  DurationUnit,
  isDurationValue,
  isMoneyValue,
  TrendData,
  ValueType,
  valueAmount,
} from '../types/insights.types'

// Locale-aware date-range options. `formatRange` collapses the shared parts on
// its own, e.g. "Jan 15 – Jun 15, 2025" within a year, "Jun 15, 2024 – Jun 15, 2025"
// across a boundary, so there's no manual same-year branch. `timeZone: 'UTC'`
// renders each boundary on its calendar day regardless of the runtime timezone
// (inputs are UTC-anchored: date-only picker values and the backend's ISO period).
const DATE_RANGE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
}

/**
 * A concise, locale-formatted date range, e.g. "Jan 15 – Jun 15, 2025" (same year)
 * or "Jun 15, 2024 – Jun 15, 2025" (spanning years). Used for the custom-range
 * label and the trend's comparison-period tooltip. `locale` defaults to `en-US`,
 * mirroring `formatMoney`, so both sides of the tooltip localise the same way.
 */
export const formatDateRange = (
  from: string,
  to: string,
  locale = 'en-US'
): string =>
  new Intl.DateTimeFormat(locale, DATE_RANGE_FORMAT)
    .formatRange(new Date(from), new Date(to))
    // Normalise the narrow space Intl puts around the range dash to a plain space,
    // so the dash renders consistently across fonts and matches the rest of the
    // UI's spacing. ICU builds differ: Node emits a thin space (U+2009) while
    // Chromium emits a narrow no-break space (U+202F), so cover both.
    .replace(/[\u2009\u202f]/g, ' ')

// Formats a {@link ValueType} into the strings the Lemonade primitives expect.
// Money is rendered through the shared `formatMoney` helpers (amount is in minor
// units); durations through `Intl`'s unit style; plain numbers go through
// `formatCount` for locale-aware thousands grouping.

// `Intl`'s singular unit identifiers for the wire's plural `DurationUnit`s. Intl
// pluralises and localises from these ("1 hour" / "2 hours" / "2h").
const INTL_DURATION_UNIT: Record<DurationUnit, string> = {
  seconds: 'second',
  minutes: 'minute',
  hours: 'hour',
  days: 'day',
}

/**
 * A duration in its own unit, e.g. `3.5 hours` (`long`), `3.5 hr` (`short`) or
 * `3.5h` (`narrow`). The magnitude is already expressed in `unit` — no rescaling,
 * so an "hours" metric reads in hours rather than being normalised to days.
 */
export const formatDuration = (
  value: number,
  unit: DurationUnit,
  {
    locale = 'en-US',
    unitDisplay = 'short',
    maximumFractionDigits,
    notation,
  }: {
    locale?: string
    unitDisplay?: 'long' | 'short' | 'narrow'
    maximumFractionDigits?: number
    /** `'compact'` abbreviates the magnitude (`1.2K hrs`) — for axis ticks. */
    notation?: 'standard' | 'compact'
  } = {}
): string =>
  new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: INTL_DURATION_UNIT[unit],
    unitDisplay,
    maximumFractionDigits,
    notation,
  }).format(value)

/**
 * Locale-aware grouping for plain (non-money) scalars — adds thousands separators
 * so counts read as `11,994`, not `11994`. `locale` defaults to `en-US`, matching
 * `formatMoney`, so number and money tiles group consistently. `maximumFractionDigits`
 * defaults to `Intl`'s standard (3); pass a higher value on precision-sensitive
 * paths (e.g. chart tooltips) so the fraction isn't silently rounded.
 */
export const formatCount = (
  value: number,
  {
    locale = 'en-US',
    maximumFractionDigits,
  }: { locale?: string; maximumFractionDigits?: number } = {}
): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value)

/**
 * A count abbreviated for somewhere space-constrained — `100K`, `1.2M` — in the
 * locale's own notation (`de-DE` gives `1,2 Mio.`, `ja-JP` gives `120万`). The
 * plain-number counterpart to `formatMoneyShort`. `locale` defaults to `en-US`,
 * matching the rest of this module.
 */
export const formatCountShort = (
  value: number,
  { locale = 'en-US' }: { locale?: string } = {}
): string =>
  new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)

/** Full hero value for a StatCard (e.g. `$1,751,382`, `3.5 hours` or `11,994`).
 * `locale` localises money (defaults to `en-US`, like `formatMoney`) and the
 * duration unit, and groups plain numbers with thousands separators. */
export const formatValueHero = (value: ValueType, locale?: string): string => {
  if (isMoneyValue(value))
    return formatMoney(
      { amount: value.amount, currency: value.currency },
      { hideDecimals: true, locale }
    )
  if (isDurationValue(value))
    return formatDuration(value.value, value.unit, {
      locale,
      unitDisplay: 'long',
    })

  return formatCount(value, { locale })
}

/**
 * A tick formatter for chart axes. Derives the unit from a sample value (every
 * value on one axis shares a unit — currency for money, the {@link DurationUnit}
 * for a duration), so the chart frame formats its numeric axis consistently even
 * though Recharts only hands the formatter a bare number.
 *
 * Compact by design, in every unit: an axis tick has little room, so money
 * abbreviates (`$1.8M`), a duration takes the narrow unit at a compact magnitude
 * (`3.5h`, `1.2Kh`) and a plain count abbreviates too (`100K`, `1.2M`) rather than
 * printing every digit and overflowing the axis gutter. A sample whose unit can't
 * be determined is treated as a count — never raw digits.
 */
export const tickFormatterFor =
  (sample: ValueType | undefined, locale?: string) =>
  (amount: number): string => {
    // The guards narrow `unknown`, so an absent sample simply falls through to the
    // count branch — no separate undefined case to keep in step.
    if (isMoneyValue(sample))
      return formatMoneyShort({ amount, currency: sample.currency }, { locale })
    if (isDurationValue(sample))
      return formatDuration(amount, sample.unit, {
        locale,
        unitDisplay: 'narrow',
        notation: 'compact',
        maximumFractionDigits: 1,
      })

    return formatCountShort(amount, { locale })
  }

/**
 * {@link tickFormatterFor}'s counterpart: the same dispatch over {@link ValueType},
 * rendering the *full* value (`$1,751,382`, not `$1.8M`) — for tooltip rows, where
 * precision matters and space doesn't.
 */
export const valueFormatterFor =
  (sample: ValueType | undefined, locale?: string) =>
  (amount: number): string => {
    if (isMoneyValue(sample))
      return formatMoney(
        { amount, currency: sample.currency },
        { hideDecimals: true, locale }
      )
    // Same "precision matters" reasoning as the plain-number path below: keep the
    // full fraction rather than letting Intl's 3-digit default round it away.
    if (isDurationValue(sample))
      return formatDuration(amount, sample.unit, {
        locale,
        maximumFractionDigits: 20,
      })

    // Group thousands but keep the full fraction (Intl's 3-digit default would
    // silently round it).
    return formatCount(amount, { locale, maximumFractionDigits: 20 })
  }

const MS_PER_MINUTE = 60_000
const MS_PER_HOUR = 60 * MS_PER_MINUTE
const MS_PER_DAY = 24 * MS_PER_HOUR

/**
 * Picks a dayjs format from the median spacing between points, so a timeseries
 * x-axis labels itself at the right granularity:
 *   minute / hourly → `14:30` (time of day)
 *   daily / weekly  → `5 Jan` (day + month)
 *   monthly         → `Jan 2025`
 *   yearly          → `2025`
 * A single point (no spacing to infer) falls back to a plain day + month.
 */
const granularityFormat = (medianGap: number): string => {
  if (medianGap <= 0) return 'D MMM'
  if (medianGap < MS_PER_HOUR) return 'HH:mm' // minute-level
  if (medianGap < MS_PER_DAY) return 'HH:mm' // hourly
  if (medianGap < 7 * MS_PER_DAY) return 'D MMM' // daily
  if (medianGap < 28 * MS_PER_DAY) return 'D MMM' // weekly
  if (medianGap < 360 * MS_PER_DAY) return 'MMM YYYY' // monthly

  return 'YYYY' // yearly
}

/**
 * A formatter for a timeseries x-axis: parses each value as a date and renders it
 * at the granularity inferred from the points' spacing (see {@link granularityFormat}).
 * Non-date values fall through unformatted.
 */
export const dateTickFormatter = (
  values: readonly string[]
): ((value: string) => string) => {
  const times = values
    .map((value) => dayjs(value))
    .filter((d) => d.isValid())
    .map((d) => d.valueOf())
    .sort((a, b) => a - b)
  const gaps = times.slice(1).map((time, index) => time - times[index])
  const medianGap = gaps.length
    ? [...gaps].sort((a, b) => a - b)[Math.floor(gaps.length / 2)]
    : 0

  const format = granularityFormat(medianGap)

  return (value) => {
    const date = dayjs(value)

    return date.isValid() ? date.format(format) : String(value)
  }
}

/**
 * Above this magnitude a percentage change stops being meaningful (e.g. a jump
 * from a near-zero baseline yields `300,000%`), so the badge drops the number and
 * shows just the arrow, surfacing the exact figure on hover via `percentLabel`.
 */
export const TREND_DISPLAY_CAP_PERCENT = 1000

/**
 * Maps a metric's structured {@link TrendData} to the StatCard trend badge. The
 * arrow direction comes from the sign of the `difference` (current − prior), which
 * is correct even against a negative baseline; the `percentage` (0–1 → e.g. `0.25`
 * → `25%`) is the badge magnitude, shown unsigned and comma-grouped ("↑ 1,250%",
 * not "↑ +1250%"). The backend omits `percentage` when the baseline is non-positive
 * (pbp-api#13174), so `value` is then `undefined` and the badge shows just the
 * arrow. A percentage above {@link TREND_DISPLAY_CAP_PERCENT} is likewise dropped
 * from the badge (`value` undefined) but its exact figure is returned as
 * `percentLabel` for the caller to surface on hover. `previousValue` is the
 * baseline and `period` its formatted date range; together they give the tooltip
 * its default "{period} ({previousValue})" reference point (a host can override the
 * period copy via `trendTooltip`). `locale` localises both; see {@link formatDateRange}.
 */
export const formatTrend = (
  trend: TrendData | undefined,
  locale?: string
):
  | {
      value?: string
      direction: 'up' | 'down'
      previousValue: string
      period: string
      /** Exact grouped percentage (e.g. `300,000%`) for the hover tooltip when the
       * magnitude is capped out of the badge; absent otherwise (shown inline, or
       * no percentage at all). */
      percentLabel?: string
    }
  | undefined => {
  if (!trend) return undefined

  const magnitude =
    trend.percentage === undefined
      ? undefined
      : Math.abs(Math.round(trend.percentage * 100))
  const percentLabel =
    magnitude === undefined ? undefined : `${formatCount(magnitude)}%`
  const capped =
    magnitude !== undefined && magnitude > TREND_DISPLAY_CAP_PERCENT

  return {
    value: capped ? undefined : percentLabel,
    direction: valueAmount(trend.difference) >= 0 ? 'up' : 'down',
    previousValue: formatValueHero(trend.value, locale),
    period: formatDateRange(trend.period.from, trend.period.to, locale),
    percentLabel: capped ? percentLabel : undefined,
  }
}

/**
 * Assembles the trend badge's hover tooltip: the capped-magnitude percentage (present only when
 * the badge dropped it) prefixed to the comparison window with the prior value appended, e.g.
 * "300,000%, Jan 1 – 31, 2025 ($1,000)". `windowOverride` replaces the trend's own date range
 * with caller-supplied copy (e.g. "vs the previous 30 days"); the prior value is always appended.
 */
export const buildTrendTooltip = (
  trend: NonNullable<ReturnType<typeof formatTrend>>,
  windowOverride?: string
): string =>
  [
    trend.percentLabel,
    `${windowOverride ?? trend.period} (${trend.previousValue})`,
  ]
    .filter(Boolean)
    .join(', ')
